<template>
  <div class="flex h-screen overflow-hidden">
    <!-- 사이드바: 항상 렌더링 -->
    <AppSidebar
      :token-usage="tokenUsage"
      :show-chat-features="false"
      :show-history-panel="false"
      :playground-info-text="'팀/직책별 권한의 차이를\n쉽게 확인할 수 있습니다.'"
      @new-chat="createNewChat"
    />
    <!-- 메인 컨테이너 -->
    <div class="flex-1 flex flex-col min-h-0" :class="{ 'pl-10': isSidebarHidden }">
      <!-- 상단 네비게이션 -->
      <div class="flex items-center justify-between p-4 bg-white relative z-[100]">
        <div class="flex items-center" :class="{ 'pl-3 mr-0 ml-3': isSidebarHidden }">
          <button
            v-if="isSidebarHidden"
            @click="toggleSidebar"
            title="사이드바 열기"
          >
            <img src="@/assets/icons/bar_side.png" alt="사이드바 열기" class="h-6 w-6" />
          </button>
          <div class="w-[400px]" v-if="!showHistoryPanel">
            <AppModelSelect
              :selected-model="selectedModel"
              :placeholder="modelPlaceholder"
              :dropdown-open="openedDropdown === 'model'"
              @select-model="selectModel"
              @dropdown-opened="onDropdownOpened('model')"
            />
          </div>
        </div>
        <div class="flex items-center gap-4 debug-disabled">
          <AppHamburgerMenu
            :show-debug="false"
            :debug-mode="debugMode"
            @new-chat="createNewChat"
          />
          <AppUserMenu
            :user-name="userName"
            :user-email="userEmail"
            :debug-mode="debugMode"
            @logout="logout"
          />
        </div>
      </div>

      <!-- 모델 및 역할 선택 영역 -->
      <div :class="{ 'pl-6': isSidebarHidden }" class="bg-white relative z-[150]">
        <!-- 역할 선택 영역 -->
        <div class="grid grid-cols-2 relative">
          <!-- 왼쪽 역할 선택 -->
          <div class="p-0 mr-0 ml-4" style="max-width:300px; max-height:100px;">
            <RoleSelect
              v-model="leftRole"
              :placeholder="leftRolePlaceholder"
              :dropdown-open="openedDropdown === 'leftRole'"
              @role-selected="handleLeftRoleSelected"
              @dropdown-opened="onDropdownOpened('leftRole')"
            />
          </div>

          <!-- 오른쪽 역할 선택 -->
          <div class="p-0 mr-0 ml-1" style="max-width:300px;">
            <RoleSelect
              v-model="rightRole"
              :placeholder="rightRolePlaceholder"
              :dropdown-open="openedDropdown === 'rightRole'"
              @role-selected="handleRightRoleSelected"
              @dropdown-opened="onDropdownOpened('rightRole')"
            />
          </div>
        </div>
      </div>

      <!-- 채팅 컨테이너 -->
      <div class="flex-1 flex flex-col min-h-0 z-0">
        <!-- 채팅 영역 -->
        <div class="flex-1 grid grid-cols-2 min-h-0">
          <!-- 왼쪽 채팅 영역 -->
          <div class="flex flex-col min-h-0">
            <div class="flex-1 overflow-y-auto overflow-x-hidden p-4 bg-white" ref="leftChatContainer">
              <div class="w-full max-w-[600px] mx-auto min-h-[48px] flex flex-col h-full" :class="{ 'pl-12': isSidebarHidden }">
                <div v-if="leftMessages.length === 0" class="flex flex-col items-center justify-center h-full">
                  <h1 class="text-2xl font-bold text-gray-700 mb-8" title="새로운 대화 시작">무엇을 도와드릴까요?</h1>
                </div>
                <div v-else>
                  <ChatMessage
                    ref="leftChatMessage"
                    :messages="leftMessages"
                    :debug-mode="false"
                    :show-date="false"
                    :is-waiting-response="isWaitingResponse"
                    @edit-message="handleEditLeftMessage"
                    @show-error-modal="handleErrorModal"
                    @show-filtering-modal="handleFilteringModal"
                    @show-masking-modal="handleMaskingModal"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- 오른쪽 채팅 영역 (구분선 추가) -->
          <div class="flex flex-col min-h-0 border-l border-gray-200">
            <div class="flex-1 overflow-y-auto overflow-x-hidden p-4 bg-white" ref="rightChatContainer">
              <div class="w-full max-w-[600px] mx-auto min-h-[48px] flex flex-col h-full">
                <div v-if="rightMessages.length === 0" class="flex flex-col items-center justify-center h-full">
                  <h1 class="text-2xl font-bold text-gray-700 mb-8" title="새로운 대화 시작">무엇을 도와드릴까요?</h1>
                </div>
                <div v-else>
                  <ChatMessage
                    ref="rightChatMessage"
                    :messages="rightMessages"
                    :debug-mode="false"
                    :show-date="false"
                    :is-waiting-response="isWaitingResponse"
                    @edit-message="handleEditRightMessage"
                    @show-error-modal="handleErrorModal"
                    @show-filtering-modal="handleFilteringModal"
                    @show-masking-modal="handleMaskingModal"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 메시지 입력 영역 -->
        <div class="p-4 bg-white">
          <div class="w-full max-w-[600px] mx-auto min-h-[48px]">
            <ChatInput
              ref="chatInput"
              :validation-fn="validateBeforeSend"
              @send-message="handleMessageSuccess"
              @pause="handlePause"
              :is-waiting-response="isWaitingResponse"
              :is-typing="isWaitingResponse"
              :messages="[...leftMessages, ...rightMessages]"
              :disabled="!canSendMessage"
              :min-height="48"
              :chat-id="'role-playground_disabled'"
              :model-name="selectedModel?.name || 'role-playground'"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-if="showRoleSelectModal" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
      <div
        class="bg-white rounded-lg p-6 shadow-lg text-center"
        style="min-width: 100px; max-width: 90vw; width: 400px;"
      >
        <div class="mb-4 text-base font-semibold" title="팀/직책 선택">팀/직책을 선택해주세요</div>
        <button
          class="px-2 py-1 bg-blue-100 hover:bg-blue-200 rounded"
          @click="showRoleSelectModal = false" title="확인">확인</button>
      </div>
    </div>

    <AlertModal :visible="showAlertModal" :message="alertMessage" @confirm="showAlertModal = false" />
    
    <!-- 마스킹 모달 -->
    <AlertModal
      v-if="showMaskingModal"
      :visible="showMaskingModal"
      :message="''"
      size="wide"
    >
      <template #default>
        <div class="mb-2 mt-6 text-red-400 font-semibold" title="마스킹 안내">개인정보 있어요. 마스킹 표시 해주세요</div>
        <div class="w-full border-none p-2 mt-6 mb-2 rounded min-h-[48px]" style="white-space:pre-wrap;word-break:break-all;">
          <span v-for="(part, i) in getMaskedParts()" :key="'part-'+i">
            <template v-if="part.type === 'pii'">
              <input type="checkbox" v-model="maskingState.masked[part.idx]" @change="updateMasked" class="no-focus-checkbox" style="margin-right:2px;vertical-align:middle;" tabindex="-1" title="마스킹" />
              <span class="font-mono bg-yellow-100 px-1 rounded" style="margin-right:2px;vertical-align:middle;">
                {{ getMaskedText(part, maskingState.masked[part.idx]) }} [{{ getMaskedTypeKor(part) }}]
              </span>
            </template>
            <template v-else>
              {{ part.text }}
            </template>
          </span>
        </div>
        <div class="flex justify-center mt-5 mb-2">
          <button 
            class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 min-w-[150px]" 
            :disabled="isWaitingResponse" 
            @click="resendWithMaskingOverride"
            title="개인정보 체크 후 전송"
          >
            개인정보 체크 후 전송
          </button>
        </div>
        <div v-if="isWaitingResponse && maskingState" class="flex flex-row items-center justify-center mt-6 h-6">
          <span class="flex items-center text-gray-500 font-semibold" style="font-size:1.1em;">
            <svg class="animate-spin h-5 w-5 text-gray-400 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
            답변을 작성 중입니다
          </span>
        </div>
      </template>
    </AlertModal>

    <!-- 필터링 모달 -->
    <FilteringModal
      :visible="showFilteringModal"
      :error-message="filteringErrorMessage"
      :loading="isWaitingResponse"
      :is-file-pii="filteringErrorCode === '2106'"
      :single-button="filteringSingleButton"
      @cancel="handleFilteringCancel"
      @confirm="resendWithFilteringOverride"
    />
  </div>
</template>

<script>
import AppHamburgerMenu from '@/components/navigation/HamburgerMenu.vue'
import AppUserMenu from '@/components/navigation/UserMenu.vue'
import AppModelSelect from '@/components/navigation/ModelSelect.vue'
import ChatInput from '@/components/chat/ChatInput.vue'
import AppSidebar from '@/components/navigation/Sidebar.vue'
import { roleAPI, authAPI, chatAPI } from '@/utils/api'
import userInfoMixin from '@/mixins/userInfo'
import { getAllSupportedModels, findModelById } from '@/utils/models'
import ChatMessage from '@/components/chat/ChatMessage.vue'
import RoleSelect from '@/components/navigation/RoleSelect.vue'
import { mapState, mapActions } from 'vuex'
import AlertModal from '@/components/common/AlertModal.vue'
import FilteringModal from '@/components/chat/FilteringModal.vue'
import { getErrorMessage } from '@/utils/errorCodes'


export default {
  name: 'RolePlayground',
  mixins: [userInfoMixin],
  components: {
    AppHamburgerMenu,
    AppUserMenu,
    AppModelSelect,
    ChatInput,
    AppSidebar,
    ChatMessage,
    RoleSelect,
    AlertModal,
    FilteringModal
  },
  data() {
    return {
      showModelDropdown: false,
      ...(() => {
        const _def = (getAllSupportedModels && typeof getAllSupportedModels === 'function') ? getAllSupportedModels()[0] : { id:'model', name:'model' };
        return {
          selectedModel: { id: _def.id, name: _def.name },
          modelPlaceholder: _def.name,
        };
      })(),
      leftRole: null,
      rightRole: null,
      leftMessages: [],
      rightMessages: [],
      tokenUsage: {
        percentage: 0,
        used: 0,
        total: 0
      },
      leftModel: null,
      rightModel: null,
      isWaitingResponse: false,
      currentResponseController: null,
      debugMode: false,
      isPaused: false,
      leftRolePlaceholder: '팀과 직책을 선택해주세요',
      rightRolePlaceholder: '팀과 직책을 선택해주세요',
      openedDropdown: null,
      showRoleSelectModal: false,
      showHistoryPanel: false,
      showAlertModal: false,
      alertMessage: '',
      maskingState: null,
      showMaskingModal: false,
      showFilteringModal: false,
      filteringErrorMessage: '',
      filteringErrorCode: '',
      filteringSingleButton: false,
      originalPayload: null,
      overrideOptions: {},
      maskingParts: [] // 마스킹 파트 배열 추가
    }
  },
  computed: {
    ...mapState(['sidebarVisible']),
    canSendMessage() {
      return (
        this.selectedModel &&
        this.leftRole && this.leftRole.team && this.leftRole.position &&
        this.rightRole && this.rightRole.team && this.rightRole.position
      );
    },
    isSidebarHidden() {
      return !this.sidebarVisible;
    }
  },
  watch: {
    leftRole(val) {
      if (val === null) {
        this.leftMessages = [];
      }
    },
    rightRole(val) {
      if (val === null) {
        this.rightMessages = [];
      }
    }
  },
  methods: {
    ...mapActions(['toggleSidebar', 'setSidebarVisible']),
    toggleSidebar() {
      this.toggleSidebar();
    },
    toggleModelDropdown() {
      this.showModelDropdown = !this.showModelDropdown
    },
    selectModel(model) {
      const found = findModelById(model.id);
      if (!found) return;
      this.selectedModel = found;
      this.modelPlaceholder = found.name;
      this.leftModel = found;
      this.rightModel = found;
      this.leftMessages = [];
      this.rightMessages = [];
      this.leftRole = null;
      this.rightRole = null;
      console.log('leftRole', this.leftRole, 'rightRole', this.rightRole);
    },
    async loadRoles() {
      try {
        const response = await roleAPI.getRoles()
        this.availableRoles = response.data
        
        // 초기 역할 설정
        if (response.data.length > 0) {
          this.leftRole = response.data[0]
          this.rightRole = response.data[1] || response.data[0]
        }
      } catch (error) {
        console.error('역할 목록 조회 중 오류 발생:', error)
      }
    },
    async handleEditLeftMessage({ messageId, content }) {
      try {
        const messageIndex = this.leftMessages.findIndex(msg => msg.id === messageId)
        if (messageIndex === -1) return
        
        // 메시지 업데이트
        const updatedMessage = {
          ...this.leftMessages[messageIndex],
          content,
          timestamp: Date.now()
        }
        
        this.leftMessages.splice(messageIndex, 1, updatedMessage)
      } catch (error) {
        console.error('메시지 수정 중 오류 발생:', error)
      }
    },
    async handleEditRightMessage({ messageId, content }) {
      try {
        const messageIndex = this.rightMessages.findIndex(msg => msg.id === messageId)
        if (messageIndex === -1) return
        
        // 메시지 업데이트
        const updatedMessage = {
          ...this.rightMessages[messageIndex],
          content,
          timestamp: Date.now()
        }
        
        this.rightMessages.splice(messageIndex, 1, updatedMessage)
      } catch (error) {
        console.error('메시지 수정 중 오류 발생:', error)
      }
    },
    validateBeforeSend(/* { content, files } */) {
      // 모델 미선택 시 사전 검증 실패
      if (!this.selectedModel) {
        this.showModelSelectModal = true;
        return false; // 전송 중단
      }
      
      // 역할 미선택 시 사전 검증 실패
      if (!this.leftRole || !this.leftRole.team || !this.leftRole.position ||
          !this.rightRole || !this.rightRole.team || !this.rightRole.position) {
        this.showRoleSelectModal = true;
        return false; // 전송 중단
      }
      
      return true; // 전송 허용
    },
    async handleMessageSuccess(userMessage) {
      console.log('[RolePlayground] handleMessageSuccess 진입', userMessage);
    
      const content = userMessage?.content || '';
      const files = userMessage?.files || [];
      
      if (!content && files.length === 0) return;
      if (this.isWaitingResponse) return;
      
      this.isWaitingResponse = true;
      
      try {
        // ✅ SDK 호출 직전 시간 기록 (메인 Chat.vue와 동일)
        const requestTime = Date.now();
        
        // 좌측 사용자 메시지
        const leftUserMessage = {
          id: 'user_' + requestTime + '_left',
          role: 'user',
          content: content,
          timestamp: requestTime,
          modelName: this.selectedModel?.name || this.selectedModel?.id || ''
        };
        
        // 우측 사용자 메시지  
        const rightUserMessage = {
          id: 'user_' + requestTime + '_right',
          role: 'user',
          content: content,
          timestamp: requestTime,
          modelName: this.selectedModel?.name || this.selectedModel?.id || ''
        };
        
        // 파일 첨부 처리
        if (files && files.length > 0) {
          const uniqueFiles = files.filter((f, i, arr) => 
            arr.findIndex(item => item.id === f.id) === i
          );
          
          const attachments = uniqueFiles.map(f => ({
            id: f.id,
            size: f.size,
            name: f.name,
            mime_type: f.mime_type || 'application/pdf'
          }));
          
          leftUserMessage.metadata = { attachments };
          rightUserMessage.metadata = { attachments };
        }
        
        // 사용자 메시지 즉시 추가
        this.leftMessages = [...this.leftMessages, leftUserMessage];
        this.rightMessages = [...this.rightMessages, rightUserMessage];
        
        // 로딩 메시지 추가
        const leftLoadingMsg = {
          id: 'loading_' + requestTime + '_left',
          role: 'loading',
          content: content,
          modelName: this.selectedModel?.name || this.selectedModel?.id || '',
          timestamp: null
        };
        
        const rightLoadingMsg = {
          id: 'loading_' + requestTime + '_right',
          role: 'loading',
          content: content,
          modelName: this.selectedModel?.name || this.selectedModel?.id || '',
          timestamp: null
        };
        
        this.leftMessages = [...this.leftMessages, leftLoadingMsg];
        this.rightMessages = [...this.rightMessages, rightLoadingMsg];
        this.scrollToBottom();
        
              // ✅ 입력창 즉시 초기화 (메시지 추가 후 바로)
      if (this.$refs.chatInput) {
        this.$refs.chatInput.resetState();
      }
      
      // ✅ 기존 우회 로직으로 서버 요청 (변경 없음)
      await this.sendMessageWithValidation(userMessage, leftLoadingMsg, rightLoadingMsg);
        
      } catch (err) {
        console.error('[RolePlayground] handleMessageSuccess 오류:', err);
        this.alertMessage = '잠시후 다시 시도해주세요';
        this.showAlertModal = true;
        this.isWaitingResponse = false;
      }
    },
    handlePause() {
      this.isPaused = true;
      if (this.currentResponseController) {
        this.currentResponseController.abort();
        this.currentResponseController = null;
      }
      this.isWaitingResponse = false;
      // 로딩 메시지 제거
      this.leftMessages = this.leftMessages.filter(m => m.role !== 'loading');
      this.rightMessages = this.rightMessages.filter(m => m.role !== 'loading');
    },
    async logout() {
      // 로그아웃 요청을 기다리지 않고 바로 리다이렉트
      this.$router.push('/login')
      // 백그라운드에서 로그아웃 처리
      try {
        await authAPI.logout()
      } catch (error) {
        console.error('로그아웃 중 오류 발생:', error)
      }
    },
    async handleLeftRoleSelected(role) {
      this.leftRole = role ? { ...role } : null;
      this.leftMessages = [];

      if (role) {
        // 역할 선택 시점에 JWT 발급
        try {
          const { getRoleJWT } = await import('@/utils/api');
          const userInfo = JSON.parse(localStorage.getItem('userInfo'));

          if (userInfo?.email) {
            await getRoleJWT(role.team, role.position, userInfo.email);
            console.log(`[Left Role] JWT 발급 완료: ${role.team}-${role.position}`);
          }
        } catch (error) {
          console.error(`[Left Role] JWT 발급 실패:`, error);
        }

        this.leftMessages.push({
          id: Date.now().toString(),
          role: 'system',
          content: `${role.team} ${role.position} 역할이 선택되었습니다.`,
          timestamp: null
        });
      }
    },

    async handleRightRoleSelected(role) {
      this.rightRole = role ? { ...role } : null;
      this.rightMessages = [];

      if (role) {
        // 역할 선택 시점에 JWT 발급
        try {
          const { getRoleJWT } = await import('@/utils/api');
          const userInfo = JSON.parse(localStorage.getItem('userInfo'));

          if (userInfo?.email) {
            await getRoleJWT(role.team, role.position, userInfo.email);
            console.log(`[Right Role] JWT 발급 완료: ${role.team}-${role.position}`);
          }
        } catch (error) {
          console.error(`[Right Role] JWT 발급 실패:`, error);
        }

        this.rightMessages.push({
          id: Date.now().toString(),
          role: 'system',
          content: `${role.team} ${role.position} 역할이 선택되었습니다.`,
          timestamp: null
        });
      }
    },
    // 스크롤을 맨 아래로 이동하는 메소드 추가
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.leftChatContainer) {
          this.$refs.leftChatContainer.scrollTop = this.$refs.leftChatContainer.scrollHeight
        }
        if (this.$refs.rightChatContainer) {
          this.$refs.rightChatContainer.scrollTop = this.$refs.rightChatContainer.scrollHeight
        }
      })
    },
    // 좌측 응답 처리 함수
    async processLeftResponse(leftResponse, leftLoadingMsg) {
      const leftLoadingIdx = this.leftMessages.findIndex(m => m.id === leftLoadingMsg.id);
      
      // 에러 응답 처리
      if (leftResponse?.status && leftResponse.status !== 200) {
        // console.log('[RolePlayground] 좌측 에러 응답:', leftResponse);
        // console.log('[RolePlayground] leftLoadingIdx:', leftLoadingIdx);
        const errorMessage = await (this.$refs.leftChatMessage?.handleErrorResponse(leftResponse) || Promise.resolve('에러가 발생했습니다.'));
        // console.log('[RolePlayground] 좌측 에러 메시지:', errorMessage);
        if (leftLoadingIdx !== -1) {
          this.leftMessages.splice(leftLoadingIdx, 1, {
            id: 'assistant_error_' + Date.now(),
            role: 'assistant',
            content: errorMessage,
            timestamp: Date.now(),
            modelName: this.selectedModel?.name || this.selectedModel?.id || '',
            isError: true
          });
          // console.log('[RolePlayground] 좌측 메시지 교체 완료');
        }
        return;
      }
      
      let leftContent = leftResponse?.content || leftResponse?.message || '';
      if (typeof leftContent !== 'string') leftContent = JSON.stringify(leftContent);
      try {
        const obj = JSON.parse(leftContent);
        if (obj && typeof obj === 'object' && obj.content) leftContent = obj.content;
      } catch (e) { /* 무시 */ }
      
      if (!leftResponse || (!leftResponse.content && !leftResponse.message)) {
        if (leftLoadingIdx !== -1) {
          this.leftMessages.splice(leftLoadingIdx, 1, {
            id: 'assistant_error_' + Date.now(),
            role: 'assistant',
            content: '현재 선택한 역할에서 일시적으로 응답이 지연되거나 연결이 원활하지 않습니다. 잠시 후 다시 시도해 주세요.',
            timestamp: null,
            modelName: this.selectedModel?.name || this.selectedModel?.id || ''
          });
        }
      } else {
        if (leftLoadingIdx !== -1) this.leftMessages.splice(leftLoadingIdx, 1);
        this.leftMessages = this.leftMessages.filter(m => m.role !== 'loading');
        
        // ✅ created_at 시간을 미리 저장 (타이핑 완료 후 사용)
        let responseTimestamp = Date.now(); // 기본값
        if (leftResponse.created_at) {
          responseTimestamp = new Date(leftResponse.created_at).getTime();
        }
        
        const assistantMsg = {
          id: 'assistant_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
          role: 'assistant',
          content: '',
          timestamp: null,
          modelName: this.selectedModel?.name || this.selectedModel?.id || '',
          encryptedContent: leftResponse?.message || leftResponse?.encryptedContent || '',
          metadata: leftResponse?.metadata || null,
          reference_docs: []
        };
        this.leftMessages = [...this.leftMessages, assistantMsg];
        const assistantIdx = this.leftMessages.length - 1;
        
        // 새 메시지 추가 후 즉시 스크롤
        this.scrollToBottom();
        
        let displayed = '';
        for (let i = 0; i < leftContent.length; i++) {
          if (this.isPaused) break;
          displayed += leftContent[i];
          this.$set(this.leftMessages, assistantIdx, {
            ...this.leftMessages[assistantIdx],
            content: displayed
          });
          
          // 타이핑 효과 중 주기적으로 스크롤 업데이트 (10글자마다)
          if (i % 10 === 0 || i === leftContent.length - 1) {
            this.scrollToBottom();
          }
          
          await new Promise(res => setTimeout(res, 15));
        }
        this.isPaused = false;
        
        // ✅ 타이핑 완료 후 created_at 시간과 reference_docs 함께 설정 (메인 Chat.vue와 동일)
        this.$set(this.leftMessages, assistantIdx, {
          ...this.leftMessages[assistantIdx],
          timestamp: responseTimestamp,
          reference_docs: leftResponse?.reference_docs || []
        });
      }
    },
    // 우측 응답 처리 함수
    async processRightResponse(rightResponse, rightLoadingMsg) {
      const rightLoadingIdx = this.rightMessages.findIndex(m => m.id === rightLoadingMsg.id);
      
      // 에러 응답 처리
      if (rightResponse?.status && rightResponse.status !== 200) {
        // console.log('[RolePlayground] 우측 에러 응답:', rightResponse);
        // console.log('[RolePlayground] rightLoadingIdx:', rightLoadingIdx);
        const errorMessage = await (this.$refs.rightChatMessage?.handleErrorResponse(rightResponse) || Promise.resolve('에러가 발생했습니다.'));
        // console.log('[RolePlayground] 우측 에러 메시지:', errorMessage);
        if (rightLoadingIdx !== -1) {
          this.rightMessages.splice(rightLoadingIdx, 1, {
            id: 'assistant_error_' + Date.now(),
            role: 'assistant',
            content: errorMessage,
            timestamp: Date.now(),
            modelName: this.selectedModel?.name || this.selectedModel?.id || '',
            isError: true
          });
          // console.log('[RolePlayground] 우측 메시지 교체 완료');
        }
        return;
      }
      
      let rightContent = rightResponse?.content || rightResponse?.message || '';
      if (typeof rightContent !== 'string') rightContent = JSON.stringify(rightContent);
      try {
        const obj = JSON.parse(rightContent);
        if (obj && typeof obj === 'object' && obj.content) rightContent = obj.content;
      } catch (e) { /* 무시 */ }
      
      if (!rightResponse || (!rightResponse.content && !rightResponse.message)) {
        if (rightLoadingIdx !== -1) {
          this.rightMessages.splice(rightLoadingIdx, 1, {
            id: 'assistant_error_' + Date.now(),
            role: 'assistant',
            content: '현재 선택한 역할에서 일시적으로 응답이 지연되거나 연결이 원활하지 않습니다. 잠시 후 다시 시도해 주세요.',
            timestamp: null,
            modelName: this.selectedModel?.name || this.selectedModel?.id || ''
          });
        }
      } else {
        if (rightLoadingIdx !== -1) this.rightMessages.splice(rightLoadingIdx, 1);
        this.rightMessages = this.rightMessages.filter(m => m.role !== 'loading');
        
        // ✅ created_at 시간을 미리 저장 (타이핑 완료 후 사용)
        let responseTimestamp = Date.now(); // 기본값
        if (rightResponse.created_at) {
          responseTimestamp = new Date(rightResponse.created_at).getTime();
        }
        
        const assistantMsg = {
          id: 'assistant_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
          role: 'assistant',
          content: '',
          timestamp: null,
          modelName: this.selectedModel?.name || this.selectedModel?.id || '',
          encryptedContent: rightResponse?.message || rightResponse?.encryptedContent || '',
          metadata: rightResponse?.metadata || null,
          reference_docs: []
        };
        this.rightMessages = [...this.rightMessages, assistantMsg];
        const assistantIdx = this.rightMessages.length - 1;
        
        // 새 메시지 추가 후 즉시 스크롤
        this.scrollToBottom();
        
        let displayed = '';
        for (let i = 0; i < rightContent.length; i++) {
          if (this.isPaused) break;
          displayed += rightContent[i];
          this.$set(this.rightMessages, assistantIdx, {
            ...this.rightMessages[assistantIdx],
            content: displayed
          });
          
          // 타이핑 효과 중 주기적으로 스크롤 업데이트 (10글자마다)
          if (i % 10 === 0 || i === rightContent.length - 1) {
            this.scrollToBottom();
          }
          
          await new Promise(res => setTimeout(res, 15));
        }
        this.isPaused = false;
        
        // ✅ 타이핑 완료 후 created_at 시간과 reference_docs 함께 설정 (메인 Chat.vue와 동일)
        this.$set(this.rightMessages, assistantIdx, {
          ...this.rightMessages[assistantIdx],
          timestamp: responseTimestamp,
          reference_docs: rightResponse?.reference_docs || []
        });
      }
    },
    onDropdownOpened(type) {
      this.openedDropdown = type;
    },
    closeAllDropdowns() {
      this.openedDropdown = null;
    },
    handleDocumentClick(e) {
      if (!this.$el.contains(e.target)) {
        this.closeAllDropdowns();
      }
    },
    async createNewChat() {
      await this.$router.push({ path: '/chat', query: { new: 1 } });
      this.$nextTick(() => {
        window.dispatchEvent(new CustomEvent('force-new-chat'));
      });
    },

    // 검증 처리 메서드들
    getMaskedParts() {
      if (!this.maskingState || !this.maskingState.parts) return [];
      return this.maskingState.parts;
    },

    getMaskedText(part, isMasked) {
      if (!part || !part.text) return '';
      return isMasked ? '*'.repeat(part.text.length) : part.text;
    },

    getMaskedTypeKor(part) {
      const typeMap = {
        'PERSON': '인명',
        'PHONE_NUMBER': '전화번호',
        'EMAIL_ADDRESS': '이메일',
        'CREDIT_CARD': '신용카드',
        'IBAN_CODE': 'IBAN',
        'IP_ADDRESS': 'IP주소',
        'PASSPORT': '여권번호',
        'DATE_TIME': '날짜시간',
        'NRP': '주민등록번호',
        'MEDICAL_LICENSE': '의료면허',
        'URL': 'URL',
        'US_DRIVER_LICENSE': '운전면허',
        'US_PASSPORT': '미국여권',
        'LOCATION': '위치',
        'US_SSN': '미국SSN',
        'AU_ABN': '호주ABN',
        'AU_ACN': '호주ACN',
        'AU_TFN': '호주TFN',
        'AU_MEDICARE': '호주메디케어',
        'US_BANK_NUMBER': '미국은행번호',
        'US_ITIN': '미국ITIN',
        'CRYPTO': '암호화폐',
        'UK_NHS': '영국NHS',
        'SG_NRIC_FIN': '싱가포르NRIC',
        'IN_PAN': '인도PAN',
        'IN_AADHAAR': '인도아드하르',
        'IN_VEHICLE_REGISTRATION': '인도차량등록',
        'IN_VOTER_NUMBER': '인도유권자번호'
      };
      return typeMap[part.piiType] || part.piiType;
    },

    updateMasked() {
      // 마스킹 상태 업데이트 시 호출
    },

    getMaskedUserContent() {
      if (!this.maskingState || !this.maskingState.parts) return '';
      
      let result = '';
      for (const part of this.maskingState.parts) {
        if (part.type === 'pii') {
          result += this.getMaskedText(part, this.maskingState.masked[part.idx]);
        } else {
          result += part.text;
        }
      }
      return result;
    },

    async handlePiiValidation(errorData, originalPayload) {
      this.isWaitingResponse = false;
      
      if (errorData.error_code === '2106') {
        // 파일 PII 오류 - 필터링 모달 표시
        this.filteringErrorMessage = getErrorMessage(errorData.error_code);
        this.filteringErrorCode = errorData.error_code;
        this.originalPayload = originalPayload;
        this.showFilteringModal = true;
      } else {
        // 일반 PII 오류 - 마스킹 모달 표시
        const parts = [];
        let currentIndex = 0;
        
        if (errorData.pii_entities && errorData.pii_entities.length > 0) {
          const content = originalPayload.userMessage?.content || '';
          const entities = [...errorData.pii_entities].sort((a, b) => a.start - b.start);
          
          for (let i = 0; i < entities.length; i++) {
            const entity = entities[i];
            
            if (currentIndex < entity.start) {
              parts.push({
                type: 'text',
                text: content.slice(currentIndex, entity.start)
              });
            }
            
            parts.push({
              type: 'pii',
              text: content.slice(entity.start, entity.end),
              piiType: entity.type,
              idx: i
            });
            
            currentIndex = entity.end;
          }
          
          if (currentIndex < content.length) {
            parts.push({
              type: 'text',
              text: content.slice(currentIndex)
            });
          }
        }
        
        this.maskingState = {
          parts: parts,
          masked: new Array(errorData.pii_entities?.length || 0).fill(false),
          originalContent: originalPayload.userMessage?.content || '',
          hasToxicFilter: false // 필터링 동시 발생 여부
        };
        this.originalPayload = originalPayload;
        this.showMaskingModal = true;
      }
    },

    async handleFilteringValidation(errorData, originalPayload) {
      this.isWaitingResponse = false;
      this.filteringErrorMessage = getErrorMessage(errorData.error_code);
      this.filteringErrorCode = errorData.error_code;
      this.originalPayload = originalPayload;
      this.showFilteringModal = true;
    },

    async resendWithMaskingOverride() {
      try {
        this.isWaitingResponse = true;
        
        const maskedContent = this.getMaskedUserContent();
        if (!maskedContent || maskedContent.trim().length === 0) {
          this.alertMessage = '마스킹된 메시지가 비어 있습니다. 다시 입력해 주세요.';
          this.showAlertModal = true;
          this.isWaitingResponse = false;
          return;
        }

        // 우회 옵션 누적 (기존 옵션 유지하면서 추가)
        const updatedOverrides = {
          ...this.overrideOptions,
          overridePrivacyFilter: true
        };

        // 필터링도 함께 있었다면 필터링 우회도 추가
        if (this.maskingState.hasToxicFilter) {
          updatedOverrides.overrideToxicFilter = true;
        }

        // 마스킹 모달 닫기
        this.showMaskingModal = false;
        this.maskingState = null;
        
        // 우회 옵션 상태 업데이트
        this.overrideOptions = updatedOverrides;
        
        // 마스킹된 내용으로 메시지 재전송
        await this.sendMessageWithValidation({ userMessage: { content: maskedContent } });

      } catch (err) {
        console.error('[RolePlayground.vue] 마스킹 재전송 중 오류:', err);
        this.alertMessage = '메시지 전송 중 오류가 발생했습니다.';
        this.showAlertModal = true;
        this.isWaitingResponse = false;
      }
    },

    async resendWithFilteringOverride() {
      try {
        this.isWaitingResponse = true;

        // 파일 PII(2106)인 경우 overridePrivacyFilter 사용
        const currentOverride = this.filteringErrorCode === '2106'
          ? { overridePrivacyFilter: true }
          : { overrideToxicFilter: true };

        // 우회 옵션 누적 (기존 옵션 유지하면서 추가)
        const updatedOverrides = {
          ...this.overrideOptions,
          ...currentOverride
        };

        // 필터링 모달 닫기
        this.showFilteringModal = false;
        this.filteringErrorCode = '';
        this.filteringErrorMessage = '';
        
        // 우회 옵션 상태 업데이트
        this.overrideOptions = updatedOverrides;
        
        // 원본 내용으로 메시지 재전송
        const content = this.originalPayload?.userMessage?.content || '';
        await this.sendMessageWithValidation({ userMessage: { content: content } });

      } catch (err) {
        console.error('[RolePlayground.vue] 필터링 재전송 중 오류:', err);
        this.alertMessage = '메시지 전송 중 오류가 발생했습니다.';
        this.showAlertModal = true;
        this.isWaitingResponse = false;
      }
    },

    handleFilteringCancel() {
      this.resetValidationState();
    },

    resetValidationState() {
      this.isWaitingResponse = false;
      this.originalPayload = null;
      this.maskingState = null;
      this.showMaskingModal = false;
      this.showFilteringModal = false;
      this.filteringErrorMessage = '';
      this.filteringErrorCode = '';
      this.overrideOptions = {};
    },

    async processSuccessfulResponses(leftResponse, rightResponse, content, files, leftUserId, rightUserId) {
      // 암호화 파이프라인 적용
      const { encrypted: leftEncrypted } = await this.$sdk.__encrypt__ ? { encrypted: await this.$sdk.__encrypt__(content) } : { encrypted: content };
      const { encrypted: rightEncrypted } = await this.$sdk.__encrypt__ ? { encrypted: await this.$sdk.__encrypt__(content) } : { encrypted: content };
      
      const now = Date.now();
      const leftUserMessage = {
        id: 'user_' + now,
        role: 'user',
        content: content,
        encryptedContent: leftEncrypted,
        originalContent: content,
        timestamp: now - 2000,
        modelName: this.selectedModel?.name || this.selectedModel?.id || '',
        userId: leftUserId
      };
      
      const rightUserMessage = {
        id: 'user_' + now,
        role: 'user',
        content: content,
        encryptedContent: rightEncrypted,
        originalContent: content,
        timestamp: now - 2000,
        modelName: this.selectedModel?.name || this.selectedModel?.id || '',
        userId: rightUserId
      };
      
      // 파일 첨부 처리(중복 제거)
      const pureFiles = (files || []).map(f => Object.assign({}, f));
      const uniqueFiles = [];
      const seen = new Set();
      for (const f of pureFiles) {
        const key = `${f.id}_${f.size}_${f.name}`;
        if (!seen.has(key)) {
          uniqueFiles.push(f);
          seen.add(key);
        }
      }
      
      if (uniqueFiles.length > 0) {
        leftUserMessage.metadata = {
          attachments: uniqueFiles.map(f => ({
            id: f.id,
            size: f.size,
            name: f.name,
            mime_type: f.mime_type
          }))
        };
        rightUserMessage.metadata = {
          attachments: uniqueFiles.map(f => ({
            id: f.id,
            size: f.size,
            name: f.name,
            mime_type: f.mime_type
          }))
        };
      }
      
      this.leftMessages = [...this.leftMessages, leftUserMessage];
      this.rightMessages = [...this.rightMessages, rightUserMessage];
      
      const leftLoadingMsg = {
        id: 'loading_' + Date.now(),
        role: 'loading',
        content: content,
        modelName: this.selectedModel?.name || this.selectedModel?.id || '',
        timestamp: null
      };
      
      const rightLoadingMsg = {
        id: 'loading_' + Date.now(),
        role: 'loading',
        content: content,
        modelName: this.selectedModel?.name || this.selectedModel?.id || '',
        timestamp: null
      };
      
      this.leftMessages = [...this.leftMessages, leftLoadingMsg];
      this.rightMessages = [...this.rightMessages, rightLoadingMsg];
      this.scrollToBottom();
      
      // 좌측과 우측 응답을 동시에 처리
      await Promise.all([
        this.processLeftResponse(leftResponse, leftLoadingMsg),
        this.processRightResponse(rightResponse, rightLoadingMsg)
      ]);
      
      this.scrollToBottom();
    },
    
    // 에러 모달 핸들러
    handleErrorModal(errorMessage) {
      this.alertMessage = errorMessage;
      this.showAlertModal = true;
    },
    
    // 필터링 모달 핸들러
    handleFilteringModal(data) {
      console.log('[RolePlayground] handleFilteringModal 호출됨:', data);
      this.filteringErrorCode = data.errorCode;
      this.filteringErrorMessage = data.errorMessage;
      this.filteringSingleButton = data.singleButton;
      this.showFilteringModal = true;
    },
    
    // 마스킹 모달 핸들러
    async handleMaskingModal(data) {
      try {
        const response = data.response;
        const hasToxicFilter = data.hasToxicFilter;
        
        const add = response.error.additional;
        const detected = add.detectedPii || [];
        
        this.maskingState = {
          maskedUserContent: add.maskedUserContent || '',
          userContent: add.userContent || '',
          detectedPii: detected,
          masked: detected.map(() => true),
          hasToxicFilter // 필터링 동시 발생 여부
        };
        
        // 마스킹 파트 배열 초기화
        this.initializeMaskingParts();
        
        this.showAlertModal = false;
        this.showMaskingModal = true;
      } catch (err) {
        console.error('[RolePlayground.vue] 마스킹 모달 처리 중 오류:', err);
        this.alertMessage = '마스킹 처리 중 오류가 발생했습니다.';
        this.showAlertModal = true;
      }
    },
    
    // 마스킹 파트 배열 초기화
    initializeMaskingParts() {
      if (!this.maskingState || !this.maskingState.detectedPii) {
        this.maskingParts = [];
        return;
      }
      
      const parts = [];
      let currentIndex = 0;
      const userContent = this.maskingState.userContent || '';
      
      this.maskingState.detectedPii.forEach((pii, index) => {
        // PII 이전 텍스트 추가
        if (pii.start > currentIndex) {
          parts.push({
            type: 'text',
            text: userContent.substring(currentIndex, pii.start),
            index: parts.length
          });
        }
        
        // PII 추가
        parts.push({
          type: 'pii',
          originalText: pii.originalText,
          maskedText: pii.maskedText,
          entityType: pii.entityType,
          index: index,
          isPii: true
        });
        
        currentIndex = pii.end;
      });
      
      // 마지막 텍스트 추가
      if (currentIndex < userContent.length) {
        parts.push({
          type: 'text',
          text: userContent.substring(currentIndex),
          index: parts.length
        });
      }
      
      this.maskingParts = parts;
    },

    /* --------------------------------------------------
     * RolePlayground 전용 SDK 헬퍼 (새로운 캐싱 시스템 활용)
     * -------------------------------------------------- */
    async getSdk(jwt) {
      const { getRoleSDK } = await import('@/utils/secureSDK');
      const roleKey = `role_${jwt.slice(-20)}`; // JWT 끝 20자리를 키로 사용
      return await getRoleSDK(jwt, roleKey);
    },
    // SDK 인스턴스를 직접 사용해 메시지 전송 (SDK v1.2.1: SecureMode fetch 사용)
    async sendWithJwt(sessionId, payload, overrides, jwt) {
      await this.getSdk(jwt) // SecureMode 활성화 보장
      const SDK_URL = process.env.VUE_APP_SECURE_AI_CLIENT_SDK_URL
      const response = await fetch(`${SDK_URL}/sgt/v1/secure/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': sessionId
        },
        body: JSON.stringify({ ...payload, requiredRag: true, stream: false })
      })
      const data = await response.json()
      if (data.error) throw new Error(data.error)
      return data
    },

    async sendMessageWithValidation(userMessage, leftLoadingMsg, rightLoadingMsg) {
      const content = userMessage?.content || '';
      const files = userMessage?.files || [];

      try {
        // 동적 JWT 발급 및 사용자 정보 가져오기
        const { getRoleJWT } = await import('@/utils/api');
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));

        if (!userInfo?.email) {
          this.alertMessage = '사용자 정보를 찾을 수 없습니다. 다시 로그인해주세요.';
          this.showAlertModal = true;
          this.isWaitingResponse = false;
          return;
        }

        // 좌측 및 우측 역할별 JWT 동적 발급
        const [leftJwt, rightJwt] = await Promise.all([
          getRoleJWT(this.leftRole.team, this.leftRole.position, userInfo.email),
          getRoleJWT(this.rightRole.team, this.rightRole.position, userInfo.email)
        ]);

        const leftUserId = userInfo.email;
        const rightUserId = userInfo.email;
        
        // 디버그용 콘솔 출력
        const middleJwt = jwt => {
          if (!jwt || jwt.length <= 50) return jwt;
          return jwt.slice(50, -10);
        };
        console.log('[RolePlayground] leftUserId:', leftUserId, 'rightUserId:', rightUserId);
        console.log('[RolePlayground] leftJwt_mid:', middleJwt(leftJwt), 'rightJwt_mid:', middleJwt(rightJwt));

        if (!leftJwt || !rightJwt) {
          this.alertMessage = 'JWT 발급에 실패했습니다. 팀/직책 정보를 확인하거나 다시 로그인해주세요.';
          this.showAlertModal = true;
          this.isWaitingResponse = false;
          return;
        }

        // 기본 페이로드 구성 (파일 정보 포함)
        const messagePayload = { 
          role: 'user', 
          content: content 
        };
        
        // 파일이 있으면 metadata에 추가
        if (files && files.length > 0) {
          messagePayload.metadata = {
            attachments: files.map(f => ({
              id: f.id,
              name: f.name,
              size: f.size,
              mime_type: f.mime_type || 'application/pdf'
            }))
          };
        }
        
        const basePayload = {
            model: this.selectedModel?.id,
            messages: [
              { role: 'system', content: 'you are a kind assistant. answer language is only Korean' },
              messagePayload
            ],
            stream: false,
            storeChat: false
        };

        // 기본 우회 옵션과 전달받은 overrides 병합
        const defaultOverrides = {
          overridePrivacyFilter: true,
          overrideToxicFilter: true
        };
        const leftOverrides  = { ...defaultOverrides, ...this.overrideOptions };
        const rightOverrides = { ...defaultOverrides, ...this.overrideOptions };

        // 좌/우 API 호출 동시 실행
        const [leftResult, rightResult] = await Promise.allSettled([
          this.sendWithJwt('role_playground_session_left',  basePayload, leftOverrides, leftJwt),
          this.sendWithJwt('role_playground_session_right', basePayload, rightOverrides, rightJwt)
        ]);

        // 성공적인 응답 처리 - 이제 로딩 메시지 제거하고 응답 메시지 추가
        const leftResponse  = leftResult.status  === 'fulfilled' ? leftResult.value  : null;
        const rightResponse = rightResult.status === 'fulfilled' ? rightResult.value : null;
        
        // ✅ 로딩 메시지 제거하고 응답 처리
        await Promise.all([
          this.processLeftResponse(leftResponse, leftLoadingMsg),
          this.processRightResponse(rightResponse, rightLoadingMsg)
        ]);

      } catch (err) {
        console.error('메시지 전송 중 오류:', err);
        // SDK v1.2.0: 세션 블로킹 에러 처리
        if (err.code === '8007' || (err.error && err.error.code === '8007')) {
          this.alertMessage = '이전 요청이 처리 중입니다. 잠시 후 다시 시도해주세요.';
        } else if (err.message && err.message.includes('JWT')) {
          this.alertMessage = 'JWT 인증에 실패했습니다. 다시 로그인해주세요.';
        } else {
          this.alertMessage = '잠시후 다시 시도해주세요';
        }
        this.showAlertModal = true;
      } finally {
        // 상태 초기화는 항상 수행
        this.isWaitingResponse = false;
        this.currentResponseController = null;
        
        // 토큰 사용량 갱신 - 오류가 발생해도 상위로 전파하지 않음
        try {
          console.log('[Token Usage] 토큰 사용량 갱신 진입');
          const usageRes = await chatAPI.getTokenUsage();
          if (usageRes && usageRes.data) {
            // validator 조건에 맞게 데이터 검증 및 변환
            const data = usageRes.data;
            let percentage = 0;
            
            // percentage 값 안전하게 처리
            if (typeof data.percentage === 'number' && !isNaN(data.percentage) && isFinite(data.percentage)) {
              percentage = Math.max(0, Math.min(100, data.percentage));
            } else if (typeof data.used === 'number' && typeof data.total === 'number' && data.total > 0) {
              // percentage가 없으면 used/total로 계산
              const calculated = (data.used / data.total) * 100;
              if (!isNaN(calculated) && isFinite(calculated)) {
                percentage = Math.max(0, Math.min(100, calculated));
              }
            }
            
            this.tokenUsage = {
              percentage: percentage,
              used: typeof data.used === 'number' && !isNaN(data.used) && isFinite(data.used) ? data.used : 0,
              total: typeof data.total === 'number' && !isNaN(data.total) && isFinite(data.total) ? data.total : 0
            };
            console.log('[Token Usage] 토큰 사용량 갱신 완료', this.tokenUsage);
          }
        } catch (e) {
          console.error('[Token Usage] 토큰 사용량 갱신 오류', e);
          // 오류 시 안전한 기본값으로 설정
          this.tokenUsage = {
            percentage: 0,
            used: 0,
            total: 0
          };
        }
        
        // 입력창 포커스 설정 - 오류가 발생해도 상위로 전파하지 않음
        try {
          this.$nextTick(() => {
            try {
              const chatInput = this.$refs.chatInput;
              if (chatInput && chatInput.$refs && chatInput.$refs.messageInput) {
                chatInput.$refs.messageInput.focus();
              }
            } catch (focusError) {
              console.log('입력창 포커스 설정 중 오류:', focusError);
            }
          });
        } catch (nextTickError) {
          console.log('$nextTick 실행 중 오류:', nextTickError);
        }
      }
    },

  },
  async mounted() {
    try {
      const hasUserInfo = await this.fetchUserInfo()
      if (hasUserInfo) {
        try {
          const usageRes = await chatAPI.getTokenUsage();
          if (usageRes && usageRes.data) {
            // validator 조건에 맞게 데이터 검증 및 변환
            const data = usageRes.data;
            let percentage = 0;
            
            // percentage 값 안전하게 처리
            if (typeof data.percentage === 'number' && !isNaN(data.percentage) && isFinite(data.percentage)) {
              percentage = Math.max(0, Math.min(100, data.percentage));
            } else if (typeof data.used === 'number' && typeof data.total === 'number' && data.total > 0) {
              // percentage가 없으면 used/total로 계산
              const calculated = (data.used / data.total) * 100;
              if (!isNaN(calculated) && isFinite(calculated)) {
                percentage = Math.max(0, Math.min(100, calculated));
              }
            }
            
            this.tokenUsage = {
              percentage: percentage,
              used: typeof data.used === 'number' && !isNaN(data.used) && isFinite(data.used) ? data.used : 0,
              total: typeof data.total === 'number' && !isNaN(data.total) && isFinite(data.total) ? data.total : 0
            };
          }
        } catch (e) {
          // 오류 시 안전한 기본값으로 설정
          this.tokenUsage = {
            percentage: 0,
            used: 0,
            total: 0
          };
        }
      }
    } catch (error) {
      // 데이터 조회 중 오류 발생 시 콘솔에만 출력
      // console.error('데이터 조회 중 오류 발생:', error)
    }
    document.addEventListener('click', this.handleDocumentClick);
    if (!this.selectedModel || !this.leftRole || !this.rightRole) {
      this.showRoleSelectModal = true;
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
    
    // ✅ 기본 SDK로 복구 (Role SDK 사용 후 원상복구)
    try {
      const { restoreDefaultSDK } = require('@/utils/secureSDK');
      restoreDefaultSDK();
      console.log('[RolePlayground] 기본 SDK로 복구 완료');
    } catch (e) {
      console.log('[RolePlayground] SDK 복구 중 오류:', e);
    }
  },
  created() {
    // 컴포넌트 생성 시 사용자 정보 초기화
    this.fetchUserInfo()
  }
}
</script>

<style>
.slide-enter-active,
.slide-leave-active {
  transition: width 0.3s ease;
}

.no-focus-checkbox:focus {
  outline: none !important;
  box-shadow: none !important;
}
</style> 